import React, { useEffect, useMemo, useState } from 'react'
import { Card, CARD_THEME } from '@st-operations/uikit/src/v2'
import set from 'lodash.set'
import get from 'lodash.get'

import TrendsSidebar from './trends-sidebar/index.js'
import useRequestConfig from '../../../../hooks/useRequestConfig.js'
import SettingsToggle from '_components/settings-toggle/index.jsx'
import capitalizeFirstLetter from '_lib/captalizeFirstLetter'
import useTrends from './hooks/useTrends.js'
import LineTrendsChart from '_components/line-trends-chart/index.jsx'
import AddTermsModal from './add-terms-modal/index.jsx'
import { useEventFeedsContext } from '../../context/index.jsx'
import { updateEventFeed } from '_services/data-explorer.js'
import { LOOKBACK_LABELS } from '_components/lookback-select/index.jsx'
import LookbackSelectStored from '_components/lookback-select-stored/index.jsx'
import ChartScaleSelectStored from '_components/chart-scale-select-stored/index.jsx'
import ButtonSelector from '_components/button-selector'

import './styles.scss'

export const TYPES = {
  KEYWORDS: 'keywords',
  ENTITIES: 'entities',
  CATEGORIES: 'categories',
  CONCEPTS: 'concepts',
  DOMAINS: 'domain',
}

export const TYPE_LABELS = [
  { label: 'Categories', value: TYPES.CATEGORIES },
  { label: 'Concepts', value: TYPES.CONCEPTS },
  { label: 'Domains', value: TYPES.DOMAINS },
  { label: 'Entities', value: TYPES.ENTITIES },
  { label: 'Keywords', value: TYPES.KEYWORDS },
]

const VISUALIZATIONS_PATH = 'trends'

const Trends = ({ id, section1, section2 }) => {
  const { data, dispatch, ACTIONS } = useEventFeedsContext()
  const { filters } = data

  const { field } = useRequestConfig('trends', { field: section2 || TYPES.ENTITIES })
  // const fieldSingular = capitalizeFirstLetter(field || '').substring(0, (field || '').length - 1)
  const requestConfig = useRequestConfig(`trends.${field}`)

  const [hoveredKeyword, setHoveredKeyword] = useState()
  const [linesSelection, setLinesSelection] = useState([])
  const [isAddTermsModalOpen, setIsAddTermsModalOpen] = useState(false)
  const narrowedRequestConfig = useMemo(() => {
    // eslint-disable-next-line no-unused-vars
    const { lookback, terms } = requestConfig
    return { field, lookback, terms: terms?.map((m) => m.term), metaTerms: terms, filters }
  }, [requestConfig, field, filters])

  const { data: trendsData, loading: loadingTrends } = useTrends(narrowedRequestConfig)

  useEffect(() => {
    window.history.pushState(null, '', `/data-explorer/${id}/${section1}_${field}`)
  }, [field])

  useEffect(() => {
    if (narrowedRequestConfig.terms && narrowedRequestConfig.terms.length === 0) {
      setIsAddTermsModalOpen(true)
    }
  }, [narrowedRequestConfig.terms])

  useEffect(() => {
    const terms = data?.visualizations?.trends?.[field]?.terms

    if (terms) {
      const linesSelection = terms?.reduce((obj, curr) => ({ ...obj, [curr.term]: true }), {})
      setLinesSelection(linesSelection)
    }
  }, [data?.visualizations?.trends?.[field]?.terms])

  const onLinesSelectionChange = (value) => setLinesSelection(value)

  const handleAddTermsSaveChanges = (
    e,
    { categoryTerms, conceptTerms, domainTerms, entityTerms, keywordTerms }
  ) => {
    const newValue = { ...data?.visualizations }

    if (categoryTerms)
      set(newValue, `${VISUALIZATIONS_PATH}.${TYPES.CATEGORIES}.terms`, categoryTerms)
    if (conceptTerms) set(newValue, `${VISUALIZATIONS_PATH}.${TYPES.CONCEPTS}.terms`, conceptTerms)
    if (domainTerms) set(newValue, `${VISUALIZATIONS_PATH}.${TYPES.DOMAINS}.terms`, domainTerms)
    if (entityTerms) set(newValue, `${VISUALIZATIONS_PATH}.${TYPES.ENTITIES}.terms`, entityTerms)
    if (keywordTerms) set(newValue, `${VISUALIZATIONS_PATH}.${TYPES.KEYWORDS}.terms`, keywordTerms)

    const newData = { ...data, visualizations: newValue }
    updateEventFeed(id, newData)
    dispatch({ type: ACTIONS.SET_DATA, payload: newData })
    setIsAddTermsModalOpen(false)
  }

  const handleRemoveTerm = (term) => {
    const newValue = { ...data?.visualizations }

    const path = `${VISUALIZATIONS_PATH}.${field}.terms`
    const got = get(newValue, path)
    set(
      newValue,
      path,
      got.filter((f) => f.term !== term)
    )
    const newData = { ...data, visualizations: newValue }
    updateEventFeed(id, newData)
    dispatch({ type: ACTIONS.SET_DATA, payload: newData })
  }

  const persistType = (typeValue) => {
    const newValue = { ...data?.visualizations }

    set(newValue, VISUALIZATIONS_PATH + '.field', typeValue)

    const newData = { ...data, visualizations: newValue }
    updateEventFeed(id, newData)
    dispatch({ type: ACTIONS.SET_DATA, payload: newData })
  }

  return (
    <>
      <Card className="trends__card" theme={CARD_THEME.NO_SHADOW}>
        <div className="trends">
          <div className="trends__title-container">
            <h3 className="trends__title">Trends</h3>
            <SettingsToggle
              label={`${LOOKBACK_LABELS[narrowedRequestConfig.lookback]}, ${capitalizeFirstLetter(
                requestConfig.scale || ''
              )}`}
            >
              <LookbackSelectStored visualizationsPath={`trends.${field}`} />
              <ChartScaleSelectStored visualizationsPath={`trends.${field}`} />
            </SettingsToggle>
          </div>

          <div className="trends__panel">
            <div className="trends__panel-chart">
              <div className="trends__types-container">
                <ButtonSelector
                  options={TYPE_LABELS}
                  value={field}
                  onChange={(event) => persistType(event.target.value)}
                />
              </div>

              <LineTrendsChart
                loading={loadingTrends}
                trendsData={trendsData}
                disabledKeywords={Object.keys(linesSelection).reduce(
                  (arr, curr) => (!linesSelection[curr] ? [...arr, curr] : arr),
                  []
                )}
                scale={requestConfig?.scale}
                interval={requestConfig?.interval}
                hoveredKeyword={hoveredKeyword}
                onHoveredKeywordChange={(value) => setHoveredKeyword(value)}
                emptyMessage={`Please select terms to display.`}
              />
            </div>
            <div className="trends__panel-terms">
              <TrendsSidebar
                field={field}
                loading={loadingTrends}
                title={capitalizeFirstLetter(field || '')}
                trendsData={trendsData}
                linesSelection={linesSelection}
                onLinesSelectionChange={onLinesSelectionChange}
                hoveredKeyword={hoveredKeyword}
                transformLineLabel={(label) =>
                  field === TYPES.CATEGORIES ? `/${label.split('/').splice(1).join('/\n')}` : label
                }
                onHoveredKeywordChange={(value) => setHoveredKeyword(value)}
                onSettingsClick={() => setIsAddTermsModalOpen(true)}
                onClearAllClick={(e) =>
                  handleAddTermsSaveChanges(e, {
                    categoryTerms: field === TYPES.CATEGORIES ? [] : undefined,
                    conceptTerms: field === TYPES.CONCEPTS ? [] : undefined,
                    domainTerms: field === TYPES.DOMAINS ? [] : undefined,
                    entityTerms: field === TYPES.ENTITIES ? [] : undefined,
                    keywordTerms: field === TYPES.KEYWORDS ? [] : undefined,
                  })
                }
                onRemoveValue={handleRemoveTerm}
                onAddTermQuickClick={(e, term) => {
                  handleAddTermsSaveChanges(e, {
                    categoryTerms:
                      field === TYPES.CATEGORIES ? [...requestConfig.terms, { term }] : undefined,
                    conceptTerms:
                      field === TYPES.CONCEPTS ? [...requestConfig.terms, { term }] : undefined,
                    domainTerms:
                      field === TYPES.DOMAINS ? [...requestConfig.terms, { term }] : undefined,
                    entityTerms:
                      field === TYPES.ENTITIES ? [...requestConfig.terms, { term }] : undefined,
                    keywordTerms:
                      field === TYPES.KEYWORDS ? [...requestConfig.terms, { term }] : undefined,
                  })
                }}
              />
            </div>
          </div>
        </div>
      </Card>
      <AddTermsModal
        isOpen={isAddTermsModalOpen}
        onSaveChangesClick={handleAddTermsSaveChanges}
        field={field}
        onFieldChange={(field) => {
          const newValue = { ...data?.visualizations }

          set(newValue, VISUALIZATIONS_PATH + '.field', field)

          const newData = { ...data, visualizations: newValue }
          updateEventFeed(id, newData)
          dispatch({ type: ACTIONS.SET_DATA, payload: newData })
        }}
        onClose={() => setIsAddTermsModalOpen(false)}
      />
    </>
  )
}

// Backend supports past 3 years of data
export const getPastThreeYears = () => {
  const date = new Date()

  const [year, month] = [date.getFullYear() - 3, date.getMonth() + 2]

  return `${year}-${month}`
}

export default Trends
